import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, trackEvent } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, MapPin, Clock, ChevronLeft, X, Star } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import "./PublicProfile.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND}${u}`);

const CURATED_LIMIT = 12;
const DEMO_ADDRESS = "Kızılay, Çankaya / Ankara";
const DEMO_MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Kizilay%2C+Ankara";
const DEMO_OPENING_HOURS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"]
  .map((day) => ({ day, open: "09:00", close: "20:00", closed: day === "Pazar" }));

function groupOpeningHours(hours) {
  const days = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
  return hours.reduce((groups, hour) => {
    const previous = groups[groups.length - 1];
    const dayIndex = days.indexOf(hour.day);
    if (previous && dayIndex > 0
      && days.indexOf(previous.lastDay) === dayIndex - 1
      && previous.open === hour.open
      && previous.close === hour.close
      && previous.closed === hour.closed) {
      previous.lastDay = hour.day;
    } else {
      groups.push({ ...hour, lastDay: hour.day });
    }
    return groups;
  }, []);
}

function getRef() {
  const p = new URLSearchParams(window.location.search);
  return p.get("ref") || "direct";
}

function isOpenNow(hours) {
  if (!hours || !hours.length) return null;
  const dayNames = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
  const now = new Date();
  const today = dayNames[now.getDay()];
  const h = hours.find((x) => x.day === today);
  if (!h || h.closed) return false;
  const [oh, om] = h.open.split(":").map(Number);
  const [ch, cm] = h.close.split(":").map(Number);
  const cur = now.getHours() * 60 + now.getMinutes();
  return cur >= oh * 60 + om && cur <= ch * 60 + cm;
}

const WhatsappIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 0 5.37 0 12a11.9 11.9 0 001.64 6L0 24l6.16-1.61A11.94 11.94 0 0012 24c6.63 0 12-5.37 12-12 0-3.19-1.25-6.19-3.48-8.52zM12 22a9.9 9.9 0 01-5.05-1.38l-.36-.21-3.66.96.98-3.57-.24-.37A9.94 9.94 0 012 12C2 6.48 6.48 2 12 2s10 4.48 10 10-4.48 10-10 10zm5.32-7.44c-.29-.15-1.72-.85-1.98-.95-.27-.1-.47-.15-.66.15s-.76.94-.93 1.13c-.17.2-.34.22-.63.07-.29-.15-1.23-.45-2.34-1.44-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.44.13-.59.13-.13.29-.34.44-.51.15-.17.19-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.58-.48-.5-.66-.51h-.56c-.19 0-.51.07-.78.37-.27.29-1.02 1-1.02 2.43s1.05 2.82 1.19 3.02c.15.2 2.06 3.15 4.99 4.42.7.3 1.24.48 1.66.61.7.22 1.34.19 1.84.12.56-.08 1.72-.7 1.96-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.2-.56-.34z"/></svg>
);

export default function PublicProfile() {
  const { slug, code } = useParams();
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("Tümü");
  const [subStyle, setSubStyle] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [error, setError] = useState("");
  const trackedRef = useRef({ profile: false, category: new Set() });

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get(`/public/${slug}`);
        setData(data);
      } catch (e) {
        setError("Barber bulunamadı.");
      }
    }
    load();
  }, [slug]);

  useEffect(() => {
    if (!data || trackedRef.current.profile) return;
    trackedRef.current.profile = true;
    const source = getRef();
    trackEvent(slug, { event_type: "profile_view", source });
    if (source === "qr") trackEvent(slug, { event_type: "qr_visit", source: "qr" });
  }, [data, slug]);

  useEffect(() => {
    if (!data) return;
    if (code) {
      const h = data.haircuts.find((x) => x.code === code);
      if (h) setSelected(h);
    }
  }, [data, code]);

  const filteredHaircuts = useMemo(() => {
    if (!data) return [];
    if (category === "Tümü") {
      const sorted = [...data.haircuts].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      return sorted.slice(0, CURATED_LIMIT);
    }
    let list = data.haircuts.filter((h) => h.category === category);
    if (subStyle) list = list.filter((h) => h.style === subStyle);
    return list;
  }, [data, category, subStyle]);

  const onCategory = (c) => {
    setCategory(c);
    setSubStyle(null);
    if (c !== "Tümü" && !trackedRef.current.category.has(c)) {
      trackedRef.current.category.add(c);
      trackEvent(slug, { event_type: "category_view", source: getRef(), category: c });
    }
  };

  const openHaircut = (h) => {
    setSelected(h);
    trackEvent(slug, { event_type: "haircut_view", source: getRef(), haircut_id: h.id, haircut_code: h.code, category: h.category });
    window.history.replaceState({}, "", `/${slug}/h/${h.code}`);
  };

  const closeHaircut = () => {
    setSelected(null);
    window.history.replaceState({}, "", `/${slug}`);
  };

  const whatsappUrl = (barber, message) => {
    const num = (barber.whatsapp || "").replace(/[^0-9]/g, "");
    const text = encodeURIComponent(message);
    return `https://wa.me/${num}?text=${text}`;
  };

  const openWhatsapp = (haircut = null) => {
    if (data?.barber?.is_demo) {
      toast("Demo profil — gerçek bir barbere bağlanmıyor", { description: `Bu ${data.barber.name} kurgusal bir demo profildir.` });
      return;
    }
    if (!data?.barber?.whatsapp) return;
    const msg = haircut
      ? `Merhaba, sitenizdeki ${haircut.style} ${haircut.code} modelini beğendim. Bu kesim hakkında bilgi almak istiyorum.`
      : `Merhaba, ${data.barber.name} için bilgi almak istiyorum.`;
    trackEvent(slug, {
      event_type: "whatsapp_click",
      source: getRef(),
      haircut_id: haircut?.id,
      haircut_code: haircut?.code,
      category: haircut?.category,
    });
    window.open(whatsappUrl(data.barber, msg), "_blank");
  };

  const openDirections = () => {
    if (data?.barber?.is_demo) {
      window.open(locationMapsUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (!data?.barber?.google_maps_url) return;
    trackEvent(slug, { event_type: "directions_click", source: getRef() });
    window.open(data.barber.google_maps_url, "_blank");
  };

  const openInstagram = () => {
    if (data?.barber?.is_demo) {
      toast("Demo bağlantısı", { description: "Bu demo profildir; gerçek bir Instagram hesabına yönlendirmiyor." });
      return;
    }
    if (!data?.barber?.instagram) return;
    trackEvent(slug, { event_type: "instagram_click", source: getRef() });
    window.open(`https://instagram.com/${data.barber.instagram.replace(/^@/, "")}`, "_blank");
  };

  if (error) return <div className="theme-public flex items-center justify-center min-h-screen text-neutral-400" data-testid="public-error">{error}</div>;
  if (!data) return <div className="theme-public flex items-center justify-center min-h-screen text-neutral-500" data-testid="public-loading">Yükleniyor…</div>;

  const { barber, services, reviews } = data;
  const locationAddress = barber.address || (barber.is_demo ? DEMO_ADDRESS : "");
  const locationMapsUrl = barber.google_maps_url || (barber.is_demo ? DEMO_MAPS_URL : "");
  const locationHours = barber.opening_hours?.length
    ? barber.opening_hours
    : (barber.is_demo ? DEMO_OPENING_HOURS : []);
  const open = isOpenNow(locationHours);
  const mobileLocationHours = groupOpeningHours(locationHours);
  const categoryStyles = data.category_styles || {};
  const primaryCategories = data.categories || [];
  const activeStyles = category !== "Tümü" ? (categoryStyles[category] || []) : [];
  const introText = barber.barber_bio?.trim()
    || [barber.barber_person_name, barber.barber_role].filter(Boolean).join(" · ");
  const introSentence = introText?.match(/^[^.!?]+[.!?]?/)?.[0] || introText;
  const introIdentity = [barber.barber_person_name, barber.barber_role].filter(Boolean).join(" · ");
  const shortIntro = introSentence?.length > 160 && introIdentity
    ? introIdentity
    : introSentence;

  return (
    <div className="theme-public public-profile pb-32">
      {/* Hero */}
      <div className="profile-hero relative w-full h-[48vh] min-h-[340px] max-h-[460px] overflow-hidden">
        <div className="profile-hero-media">
        <img
          src={resolveImg(barber.hero_url) || "https://images.unsplash.com/photo-1622286346003-b73a7d4b3c72?w=1600&q=80"}
          alt={barber.name}
          className="profile-hero-photo absolute inset-0 w-full h-full object-cover"
          data-testid="hero-image"
        />
        <div className="profile-hero-overlay absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/50 to-[#111111]/20" />
        <div className="profile-hero-overlay absolute inset-0 grain" />
        </div>

        <div className="profile-hero-content relative z-10 h-full flex flex-col justify-between px-6 md:px-10 pt-6 pb-8 max-w-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="hidden md:block text-[10px] tracking-[0.32em] text-white/60" data-testid="hero-brand">{barber.name.toUpperCase()}</div>
              {barber.is_demo && (
                <span className="text-[9px] tracking-[0.24em] text-[#C4A661]/80 border border-[#C4A661]/40 px-2 py-0.5 uppercase" data-testid="demo-badge">Demo Profil</span>
              )}
            </div>
          </div>

          <div>
            <h1 className="font-editorial text-4xl md:text-6xl leading-[1.02] tracking-tight" data-testid="barber-name">{barber.name}</h1>
            {(barber.short_location || barber.address) && (
              <div className="mt-3 flex items-center gap-2 text-sm text-white/80" data-testid="barber-short-location">
                <MapPin size={14} />
                <span>{barber.short_location || barber.address}</span>
              </div>
            )}
            {shortIntro && (
              <p className="profile-short-intro hidden" data-testid="hero-intro">{shortIntro}</p>
            )}

            {/* Trust indicators */}
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/75" data-testid="trust-indicators">
              {open !== null && (
                <div className="flex items-center gap-2" data-testid="open-indicator">
                  <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-emerald-400" : "bg-red-400"}`} />
                  <span>{open ? "Bugün açık" : "Şu an kapalı"}</span>
                </div>
              )}
              {barber.google_rating != null && (
                <div className="flex items-center gap-1.5" data-testid="rating-indicator">
                  <Star size={12} className="fill-[#C4A661] text-[#C4A661]" />
                  <span>{Number(barber.google_rating).toFixed(1)}</span>
                </div>
              )}
            </div>

            <div className="profile-hero-actions mt-6 flex gap-3">
              <button data-testid="hero-whatsapp" onClick={() => openWhatsapp()} className="btn-primary btn-whatsapp">
                <WhatsappIcon size={16} /> WhatsApp
              </button>
              {barber.instagram && (
                <button data-testid="hero-instagram" onClick={openInstagram} className="btn-ghost">
                  <Instagram size={16} /> Instagram
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Haircut discovery */}
      <section className="profile-discovery px-6 md:px-10 pt-14 md:pt-20 max-w-5xl">
        <div className="hidden md:block text-[10px] tracking-[0.32em] text-[#C4A661] mb-3">MODEL SEÇ</div>
        <h2 className="font-editorial text-3xl md:text-5xl leading-tight tracking-tight max-w-xl" data-testid="discovery-heading">
          Nasıl bir saç kesimi düşünüyorsun?
        </h2>

        {/* Primary category — editorial typographic nav (not filter pills) */}
        <div className="mt-8 -mx-6 md:-mx-10 px-6 md:px-10 overflow-x-auto no-scrollbar border-b border-white/10" data-testid="category-scroll">
          <div className="flex gap-6 md:gap-8 min-w-max text-xs tracking-[0.24em] uppercase">
            {["Tümü", ...primaryCategories].map((c) => {
              const active = category === c;
              return (
                <button
                  key={c}
                  onClick={() => onCategory(c)}
                  aria-pressed={active}
                  className={`whitespace-nowrap pb-3 transition-colors border-b-2 ${active ? "text-[#C4A661] border-[#C4A661]" : "text-neutral-500 border-transparent hover:text-neutral-300"}`}
                  data-testid={`cat-${c.replace(/\s+/g, "-").replace(/\//g, "").toLowerCase()}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Subcategory — lighter, secondary */}
        {activeStyles.length > 0 && (
          <div className="mt-4 -mx-6 md:-mx-10 px-6 md:px-10 overflow-x-auto no-scrollbar" data-testid="substyle-scroll">
            <div className="flex gap-5 md:gap-6 min-w-max text-sm">
              <button
                onClick={() => setSubStyle(null)}
                aria-pressed={!subStyle}
                data-testid="substyle-all"
                className={`whitespace-nowrap ${!subStyle ? "text-white" : "text-neutral-500 hover:text-neutral-300"}`}
              >
                Hepsi
              </button>
              {activeStyles.map((s) => (
                <button
                  key={s}
                  onClick={() => setSubStyle(s)}
                  aria-pressed={subStyle === s}
                  data-testid={`substyle-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`whitespace-nowrap ${subStyle === s ? "text-white" : "text-neutral-500 hover:text-neutral-300"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {category === "Tümü" && (
          <div className="mt-4 text-xs text-neutral-500" data-testid="curated-note">
            Öne çıkan çalışmalar · bir kategori seç, tüm işleri gör
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 mt-8" data-testid="haircut-grid">
          {filteredHaircuts.map((h) => (
            <button
              key={h.id}
              onClick={() => openHaircut(h)}
              className="text-left group"
              data-testid={`haircut-${h.code}`}
            >
              <div className="ar-3-4 overflow-hidden bg-neutral-900">
                <img
                  loading="lazy"
                  src={resolveImg(h.image_url)}
                  alt={h.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="mt-2 flex items-baseline justify-between gap-2">
                <span className="text-sm text-neutral-200">{h.style || h.category}</span>
                <span className="text-[10px] tracking-widest text-neutral-500">{h.code}</span>
              </div>
            </button>
          ))}
          {filteredHaircuts.length === 0 && (
            <div className="col-span-full text-sm text-neutral-500 py-10 text-center" data-testid="haircut-empty">Bu kategoride henüz görsel yok.</div>
          )}
        </div>
      </section>

      {/* Services */}
      {services.length > 0 && (
        <section className="profile-lower-section profile-services px-6 md:px-10 pt-20 max-w-5xl">
          <div className="text-[10px] tracking-[0.32em] text-[#C4A661] mb-3">FİYATLAR</div>
          <h2 className="font-editorial text-3xl md:text-5xl leading-tight tracking-tight mb-8" data-testid="services-heading">Hizmetler</h2>
          <div className="divide-y divide-white/10 border-y border-white/10" data-testid="services-list">
            {services.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-5" data-testid={`service-row-${s.id}`}>
                <div className="text-base md:text-lg">{s.name}</div>
                <div className="font-editorial text-lg md:text-2xl">₺{Math.round(s.price)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Berberini Tanı — photo-led editorial, positioned just before reviews */}
      {(barber.barber_person_name || barber.barber_bio || barber.barber_portrait_url) && (
        <section className="profile-lower-section profile-introduction px-6 md:px-10 pt-20 max-w-5xl" data-testid="meet-barber">
          <div className="text-[10px] tracking-[0.32em] text-[#C4A661] mb-6">BERBERİNİ TANI</div>
          <div className="max-w-md md:max-w-lg mx-auto md:mx-0">
            {barber.barber_portrait_url ? (
              <div className="relative w-full aspect-[4/5] overflow-hidden bg-neutral-900" data-testid="intro-portrait">
                <img src={resolveImg(barber.barber_portrait_url)} alt={barber.barber_person_name || barber.name} className="w-full h-full object-cover" />
                <div className="profile-portrait-overlay absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />
                <div className="profile-portrait-caption absolute inset-x-0 bottom-0 p-6 md:p-8">
                  {barber.barber_person_name && (
                    <div className="font-editorial text-3xl md:text-4xl leading-tight text-white" data-testid="intro-name">{barber.barber_person_name}</div>
                  )}
                  {(barber.barber_role || (barber.show_years_experience && barber.years_experience)) && (
                    <div className="mt-2 text-sm text-white/80" data-testid="intro-role">
                      {barber.barber_role}
                      {barber.barber_role && barber.show_years_experience && barber.years_experience ? " · " : ""}
                      {barber.show_years_experience && barber.years_experience ? `${barber.years_experience} yıl deneyim` : ""}
                    </div>
                  )}
                </div>
              </div>
            ) : barber.barber_person_name ? (
              <div>
                <div className="font-editorial text-3xl md:text-4xl leading-tight" data-testid="intro-name">{barber.barber_person_name}</div>
                {(barber.barber_role || (barber.show_years_experience && barber.years_experience)) && (
                  <div className="mt-2 text-sm text-neutral-400" data-testid="intro-role">
                    {barber.barber_role}
                    {barber.barber_role && barber.show_years_experience && barber.years_experience ? " · " : ""}
                    {barber.show_years_experience && barber.years_experience ? `${barber.years_experience} yıl deneyim` : ""}
                  </div>
                )}
              </div>
            ) : null}
            {barber.barber_bio && (
              <p className="mt-6 text-neutral-300 text-base md:text-lg leading-relaxed" data-testid="intro-bio">{barber.barber_bio}</p>
            )}
          </div>
        </section>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <section className="profile-lower-section profile-reviews px-6 md:px-10 pt-20 max-w-5xl">
          <div className="text-[10px] tracking-[0.32em] text-[#C4A661] mb-3">MÜŞTERİ YORUMLARI</div>
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <h2 className="font-editorial text-3xl md:text-5xl leading-tight tracking-tight" data-testid="reviews-heading">Neler diyorlar</h2>
            {barber.google_rating != null && (
              <div className="flex items-center gap-2 text-sm text-neutral-300" data-testid="reviews-rating">
                <Star size={14} className="fill-[#C4A661] text-[#C4A661]" />
                <span className="font-editorial text-2xl leading-none">{Number(barber.google_rating).toFixed(1)}</span>
                <span className="text-neutral-500 text-xs uppercase tracking-widest">Google</span>
              </div>
            )}
          </div>
          <div className="divide-y divide-white/10" data-testid="reviews-list">
            {(showAllReviews ? reviews : reviews.slice(0, 3)).map((r) => (
              <div key={r.id} className="py-6 first:pt-0" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={12} className="fill-[#C4A661] text-[#C4A661]" />
                  ))}
                </div>
                <p className="text-neutral-200 text-base md:text-lg leading-relaxed max-w-2xl">"{r.text}"</p>
                <div className="mt-3 text-xs text-neutral-500">— {r.customer_name} · {new Date(r.date).toLocaleDateString("tr-TR")}</div>
              </div>
            ))}
          </div>
          {reviews.length > 3 && (
            <button
              onClick={() => setShowAllReviews(!showAllReviews)}
              data-testid="toggle-all-reviews"
              className="mt-6 text-[11px] uppercase tracking-widest text-neutral-500 hover:text-white transition-colors"
            >
              {showAllReviews ? "Daha az göster" : `Tüm yorumları gör (${reviews.length}) →`}
            </button>
          )}
        </section>
      )}

      {/* Location */}
      {locationAddress && (
        <section className="profile-lower-section profile-location px-6 md:px-10 pt-20 max-w-5xl">
          <div className="text-[10px] tracking-[0.32em] text-[#C4A661] mb-3">DÜKKAN</div>
          <h2 className="font-editorial text-3xl md:text-5xl leading-tight tracking-tight mb-8" data-testid="location-heading"><span className="md:hidden">Neredeyiz?</span><span className="hidden md:inline">Bize gel</span></h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="surface p-6">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-[#C4A661]" />
                <div>
                  <div className="text-sm text-neutral-400 mb-1">Adres</div>
                  <div className="text-base" data-testid="location-address">{locationAddress}</div>
                </div>
              </div>
              <div className="mt-6 flex items-start gap-3">
                <Clock size={18} className="mt-1 text-[#C4A661]" />
                <div className="w-full">
                  <div className="text-sm text-neutral-400 mb-2">Çalışma saatleri</div>
                  <div className="space-y-1 text-sm" data-testid="opening-hours">
                    {locationHours.map((h) => (
                      <div key={h.day} className="hidden md:flex justify-between">
                        <span className="text-neutral-300">{h.day}</span>
                        <span className="text-neutral-400">{h.closed ? "Kapalı" : `${h.open} – ${h.close}`}</span>
                      </div>
                    ))}
                    {mobileLocationHours.map((h) => (
                      <div key={`mobile-${h.day}`} className="flex md:hidden justify-between">
                        <span className="text-neutral-300">{h.day === h.lastDay ? h.day : `${h.day}–${h.lastDay}`}</span>
                        <span className="text-neutral-400">{h.closed ? "Kapalı" : `${h.open}–${h.close}`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button data-testid="directions-btn" onClick={openDirections} className="btn-primary mt-8 w-full"><span className="md:hidden">Google Maps'te aç</span><span className="hidden md:inline">Yol Tarifi Al</span></button>
            </div>
            <div className="surface overflow-hidden min-h-[300px] relative">
              <iframe
                title="map"
                className="absolute inset-0 w-full h-full"
                src={barber.is_demo
                  ? `https://www.google.com/maps?q=${encodeURIComponent(locationAddress)}&z=15&output=embed`
                  : `https://www.google.com/maps?q=${barber.latitude || 41.0256},${barber.longitude || 28.9744}&z=15&output=embed`}
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {barber.is_demo === true && (
        <section className="profile-demo-qr" aria-labelledby="demo-qr-heading" data-testid="demo-qr-section">
          <h2 id="demo-qr-heading">QR ile profili aç</h2>
          <p className="profile-demo-qr-description">
            Müşterilerin dükkânda QR kodu okutup saç modellerini, fiyatlarını ve bilgilerini inceleyebilir.
          </p>
          <QRCodeSVG
            value="https://tarzberber.com/ornek"
            size={210}
            level="H"
            marginSize={4}
            bgColor="#FFFFFF"
            fgColor="#111111"
            title="TarzBerber demo profilini aç: https://tarzberber.com/ornek"
          />
          <p className="profile-demo-qr-caption">Kameranı aç ve QR kodu okut</p>
        </section>
      )}

      {/* Footer */}
      <div className="profile-footer px-6 md:px-10 pt-24 pb-10 max-w-5xl text-xs text-neutral-500">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="tracking-widest">
            © {new Date().getFullYear()} {barber.name.toUpperCase()}
            {barber.is_demo && <span className="ml-3 text-neutral-600">· Demo profil</span>}
          </div>
          <Link to="/login" data-testid="footer-login" className="hover:text-white transition-colors">Barber girişi</Link>
        </div>
      </div>

      {/* Haircut modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto" data-testid="haircut-modal" onClick={closeHaircut}>
          <div className="min-h-screen flex flex-col md:flex-row" onClick={(e) => e.stopPropagation()}>
            <button data-testid="modal-back" onClick={closeHaircut} className="absolute top-5 left-5 z-10 text-white/80 hover:text-white flex items-center gap-1 text-sm">
              <ChevronLeft size={18} /> Geri
            </button>
            <button data-testid="modal-close" onClick={closeHaircut} className="absolute top-5 right-5 z-10 text-white/80 hover:text-white">
              <X size={22} />
            </button>
            <div className="md:w-3/5 bg-black">
              <img src={resolveImg(selected.image_url)} alt={selected.title} className="w-full h-[60vh] md:h-screen object-cover" />
            </div>
            <div className="md:w-2/5 p-8 md:p-12 flex flex-col justify-between bg-[#111111]">
              <div>
                <div className="text-[10px] tracking-[0.32em] text-[#C4A661] mb-4">{(selected.style || selected.category || "").toUpperCase()}</div>
                <h3 className="font-editorial text-3xl md:text-4xl leading-tight mb-4" data-testid="modal-title">{selected.title}</h3>
                <div className="text-sm text-neutral-400 mb-8" data-testid="modal-code">Referans kodu · {selected.code}</div>
                {selected.description && <p className="text-neutral-300 leading-relaxed">{selected.description}</p>}
                <div className="mt-8 pt-8 border-t border-white/10 text-sm text-neutral-400">
                  <div className="text-white">{barber.name}</div>
                  {barber.address && <div className="mt-1 text-xs">{barber.address}</div>}
                </div>
              </div>
              <button
                data-testid="modal-whatsapp"
                onClick={() => openWhatsapp(selected)}
                className="btn-primary btn-whatsapp mt-10 w-full text-base py-4"
              >
                <WhatsappIcon size={18} /> Bu modeli istiyorum
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky mobile CTA */}
      {!selected && barber.whatsapp && (
        <div className="fixed bottom-4 left-4 right-4 z-40 md:hidden">
          <button data-testid="sticky-whatsapp" onClick={() => openWhatsapp()} className="btn-primary btn-whatsapp w-full py-4 shadow-lg">
            <WhatsappIcon size={18} /> WhatsApp'tan yaz
          </button>
        </div>
      )}
    </div>
  );
}
